import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/Gdservices.css";

import gd1 from "../assets/icon1.png";
import gd2 from "../assets/icon2.png";
import gd3 from "../assets/icon3.png";
import gd4 from "../assets/icon4.png";
import gd5 from "../assets/icon5.png";
import gd6 from "../assets/icon6.png";

const services = [
  {
    icon: gd1,
    title: "Logo Design",
    desc: "A logo is the foundation of your brand identity. We design unique logos that help your business stand out and create a strong first impression.",
    tags: [
      "Logo Creation",
      "Brand Logo",
      "Creative Logo",
      "Business Logo",
      "Unique Logo Design",
    ],
  },
  {
    icon: gd2,
    title: "Brand Identity Design",
    desc: "Brand identity defines the visual personality of your business. We create consistent branding that builds trust and strengthens brand recognition.",
    tags: [
      "Brand Identity",
      "Visual Branding",
      "Brand Guidelines",
      "Business Branding",
      "Corporate Identity",
    ],
  },
  {
    icon: gd3,
    title: "Brochure & Pamphlet Design",
    desc: "Brochures and pamphlets help communicate your products and services effectively. Our creative designs present your information clearly while attracting potential customers.",
    tags: [
      "Brochure Design",
      "Pamphlet",
      "Marketing Brochure",
      "Print Design",
      "Promotional Materials",
    ],
  },
  {
    icon: gd4,
    title: "Advertising Creatives",
    desc: "Engaging advertising visuals are essential for capturing attention. We design impactful creatives that increase brand awareness and support your marketing campaigns.",
    tags: [
      "Ad Creatives",
      "Marketing Graphics",
      "Campaign Design",
      "Digital Ads",
      "Promotional Creatives",
    ],
  },
  {
    icon: gd5,
    title: "Custom Graphic Design",
    desc: "Every business has unique design needs. We provide customized graphic solutions that align with your brand and effectively showcase your ideas.",
    tags: [
      "Custom Design",
      "Creative Graphics",
      "Business Visuals",
      "Unique Design Solutions",
      "Tailored Graphics",
    ],
  },
  {
    icon: gd6,
    title: "Modern Design Solutions",
    desc: "Our team uses the latest design tools and creative strategies to produce clean, modern, and visually appealing graphics that enhance your brand presence.",
    tags: [
      "Modern Design",
      "Creative Strategy",
      "Visual Innovation",
      "Professional Graphics",
      "Design Excellence",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function ServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = services.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="gd-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="gd-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`gd-services-eyebrow ${
          sectionVisible ? "gd-reveal" : ""
        }`}
      >
        What We Offer
      </span>

      <h2
        className={`gd-services-heading ${
          sectionVisible ? "gd-reveal" : ""
        }`}
        ref={headingRef}
      >
        Need for Graphic Design Service for your business
      </h2>

      <p
        className={`gd-services-subtext ${
          sectionVisible ? "gd-reveal" : ""
        }`}
        ref={subtextRef}
      >
        We create impactful graphic designs that strengthen brand identity
        and connect businesses with their audience. From logos to marketing
        creatives, our designs ensure clear communication and a strong brand
        presence.
      </p>

      <div className="gd-carousel">
        <button
          type="button"
          className="gd-carousel-arrow gd-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="gd-carousel-track">
          {services.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`gd-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="gd-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="gd-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="gd-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="gd-carousel-arrow gd-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="gd-carousel-dots">
        {services.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`gd-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default ServicesSection;